import {
  buildBillingParams,
  buildPlatformSubscriptionCheckoutParams,
  checkout,
  billing
} from "./checkout/actions";
import {
  crispComposerWithMessage,
  HELP_SUSPENDED_SUBSCRIPTION
} from "utils/crisp";
import { USER } from "./constants/models";
import { BUTTON_TAGS } from "utils/constants/ui";
import { PRODUCT_FEATURE_CAPABILITY } from "utils/constants/capability";
const UPGRADE_PROMPT = "upgrade-prompt";

/**
 * Subscribed states
 * -- Has subscribed
 * Valid - prompt toitemsSumTotal manage - "Active & can manage"
 * Can bill - prompt to manage - "Needs attention & can manage"
 * Can't bill - select a new plan - "Inactive & choose another plan"
 * -- Hasn't subscribed
 * No subs - select a new plan - "Go pick a plan"
 */
export const getUpgradeBanner = ({
  resource,
  resourceUUID,
  customerId,
  loading,
  hasSubs,
  hasNeedsAttention,
  hasSuspended,
  /**
   * Enabled describes which features are enabled (e.g. remove branding)
   */
  enabled,
  capability,
  setLoading
}) => {
  if (!customerId) {
    throw new Error("No customer id passed to upgrade banner");
  }

  const result = {};
  const checkoutSession = buildPlatformSubscriptionCheckoutParams({
    customer: customerId,
    resource: resource,
    resourceUUID: resourceUUID
  });

  const billingSession = buildBillingParams({ customer: customerId });

  if (!hasSubs) {
    result.header = "Upgrade";
    result.subheader =
      "Upgrade to remove PriceBlocs branding from payment links, enable setup mode and attach files to links.";

    result.actions = [
      {
        copy: `Select a plan`,
        tag: BUTTON_TAGS.BUTTON,
        loading: loading,
        onClick: checkout({ session: checkoutSession, setLoading })
      }
    ];
  } else if (hasNeedsAttention) {
    result.header = "Subscription inactive";
    result.subheader =
      resource === USER
        ? "Your subscription is not active. Update your subscription for continued usage of premium features or choose a new plan."
        : "Your team subscription is not active. Update your team subscription for continued usage of premium features or choose a new plan.";

    result.actions = [
      {
        copy: `Manage`,
        tag: BUTTON_TAGS.BUTTON,
        loading: loading,
        onClick: billing({ session: billingSession, setLoading })
      }
    ];
  } else if (hasSuspended) {
    result.header = "Subscription suspended";
    result.subheader =
      resource === USER
        ? "Your subscription has been suspended. If you think this has been done in error, please contact support."
        : "Your team subscription has been suspended. If you think this has been done in error, please contact support.";

    result.actions = [
      {
        copy: `Contact support`,
        tag: BUTTON_TAGS.BUTTON,
        onClick: crispComposerWithMessage(HELP_SUSPENDED_SUBSCRIPTION)
      }
    ];
  } else {
    result.header = "Upgrade";
    result.subheader =
      "Choose another plan to get a lower fee per transaction and to remove PriceBlocs branding from payment links.";

    result.actions = [
      {
        copy: `Select a plan`,
        tag: BUTTON_TAGS.BUTTON,
        loading: loading,
        onClick: checkout({ session: checkoutSession, setLoading })
      }
    ];
  }

  return result;
};

const UPGRADE_FEATURE_STATE_KEYS = Object.values(
  PRODUCT_FEATURE_CAPABILITY
).reduce((memo, feature) => {
  memo[feature] =
    process.env.NODE_ENV === "production"
      ? `${feature}-${UPGRADE_PROMPT}`
      : `${feature}-dev-${UPGRADE_PROMPT}`;
  return memo;
}, {});

export const setStoreUpgradeFeature = (feature, state) =>
  typeof window !== "undefined" &&
  window.localStorage.setItem(UPGRADE_FEATURE_STATE_KEYS[feature], state);

export const clearStoreUpgradeFeature = (feature) =>
  typeof window !== "undefined" &&
  window.localStorage.setItem(UPGRADE_FEATURE_STATE_KEYS[feature], "");

export const getStoreUpgradeFeature = (feature) =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(UPGRADE_FEATURE_STATE_KEYS[feature]);

/**
 * Reset local storage capabilities
 * Other uids located in: utils/constants/capability
 [
  "remove_branding",
  "attach_files",
  "api_payment_links",
  "developer_components",
  "transaction_fee",
].forEach((key) => localStorage.removeItem(`${key}-dev-upgrade-prompt`))

[
  "remove_branding",
  "attach_files",
  "api_payment_links",
  "developer_components",
  "transaction_fee",
].forEach((key) => localStorage.removeItem(`${key}-upgrade-prompt`))
 */
